import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router';

import type { Props as ButtonProps } from '@sorare/core/src/atoms/buttons/Button';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconTrash } from '@sorare/core/src/atoms/icons/system/iconTrash.ts';
import { ANY_SPORT_MARKET_WATCHLISTS } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { glossary } from '@sorare/core/src/lib/glossary';

import { DestroyWatchlistDialog } from '#src/components/watchlists/DestroyWatchlistDialog/index.tsx';

import type { DestroyWatchlistButton_watchlist } from './__generated__/index.graphql.ts';

type Props = {
  watchlist: DestroyWatchlistButton_watchlist;
  onClose?: () => void;
} & Pick<ButtonProps, 'color' | 'size' | 'blur'>;

export const DestroyWatchlistButton = ({
  watchlist,
  onClose,
  ...props
}: Props) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { generateSportContextPath } = useSportContext();

  return (
    <>
      <Button
        {...props}
        startIcon={<FontAwesomeIcon icon={iconTrash} size="sm" />}
        onClick={() => setOpen(true)}
      >
        <FormattedMessage {...glossary.delete} />
      </Button>
      <DestroyWatchlistDialog
        open={open}
        onClose={() => {
          setOpen(false);
          onClose?.();
        }}
        watchlist={watchlist}
        onSuccess={() => {
          navigate(generateSportContextPath(ANY_SPORT_MARKET_WATCHLISTS));
        }}
      />
    </>
  );
};

DestroyWatchlistButton.fragments = {
  watchlist: gql`
    fragment DestroyWatchlistButton_watchlist on Watchlist {
      slug
      id
      title
      ...DestroyWatchlistDialog_watchlist
    }
    ${DestroyWatchlistDialog.fragments.watchlist}
  ` as TypedDocumentNode<DestroyWatchlistButton_watchlist>,
};
